import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import QuoteModal from '@/components/QuoteModal';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close sidebar when clicking outside
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/brands', label: 'Brands' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4 lg:px-8">
          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center justify-between h-20">
            {/* Logo - Left */}
            <Link to="/" className="flex items-center space-x-3">
              <img src="/logo.png" alt="Bilal Ganj Auto Parts" className="h-12 w-auto" />
            </Link>

            {/* Navigation - Center */}
            <div className="flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    location.pathname === link.path
                      ? 'text-primary'
                      : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Service Request Button - Right */}
            <Button 
              onClick={() => setQuoteModalOpen(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Layout */}
          <div className="flex lg:hidden items-center justify-between h-20 relative">
            {/* Menu Button - Left */}
            <button
              className="text-foreground z-10"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              <Menu size={24} />
            </button>

            {/* Logo - Center */}
            <Link to="/" className="flex items-center space-x-3 absolute left-1/2 transform -translate-x-1/2 z-10">
              <img src="/logo.png" alt="Bilal Ganj Auto Parts" className="h-12 w-auto" />
            </Link>

            {/* Contact Icon - Right */}
            <Button 
              size="icon" 
              onClick={() => setQuoteModalOpen(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground z-10"
            >
              <MessageCircle size={20} />
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-45 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-background shadow-2xl z-50 lg:hidden transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header with Logo and Close Button */}
          <div className="p-6 border-b border-border flex items-center justify-between">
            <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center space-x-3">
              <img src="/logo.png" alt="Bilal Ganj Auto Parts" className="h-12 w-auto" />
            </Link>
            <button
              className="text-foreground hover:text-primary transition-colors"
              onClick={() => setIsOpen(false)}
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          {/* Sidebar Navigation */}
          <div className="flex-1 overflow-y-auto py-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-6 py-4 text-base font-medium transition-colors border-l-4 ${
                  location.pathname === link.path
                    ? 'text-primary border-primary bg-primary/10'
                    : 'text-muted-foreground border-transparent hover:text-primary hover:bg-primary/5'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Quote Modal */}
      <QuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} />
    </>
  );
};

export default Navbar;